/**
 * What the shell starts once, for the signed-in person: the width, the live
 * stream, the offline layer, a deep link, push and location. Each returns its
 * own stop, so a sign-out stops them all.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { note } from "../app/trail";
import { useMe } from "../app/me";
import { ensureSubscribed } from "../device/push";
import { granted, startTracking, stopTracking } from "../device/location";
import { openOffline } from "../offline/idb";
import { startOffline, syncNow } from "../offline/register";
import { fetchWhole } from "../offline/whole";
import { usePrefs } from "../prefs/store";
import { useRecorder } from "../recorder/store";
import { providerName } from "../widgets/settings/providers";
import { widgetsFor } from "../widgets/registry";
import { CONNECTIONS } from "./connections";
import { readDeepLink } from "./deepLink";
import { startLive, useLive } from "./live";
import { useNotices } from "./notices";
import { focusedTileIndex } from "./layout";
import { followWidth, useLayout } from "./useLayout";

async function whole(): Promise<void> {
  const db = await openOffline();
  try {
    await fetchWhole(db);
  } finally {
    db.close();
  }
}

/** The page's own errors, into the action log. Returns its stop. (Here, not
 * in `trail.ts`: the service worker's build reaches that file, and has no
 * `window`.) */
export function watchErrors(): () => void {
  const onError = (event: ErrorEvent) => note("error", event.message || String(event.error));
  const onRejection = (event: PromiseRejectionEvent) =>
    note("error", event.reason instanceof Error ? event.reason.message : String(event.reason));
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}

export function useShellBoot(): void {
  const client = useQueryClient();
  const me = useMe();
  const location = usePrefs((s) => s.location);

  useEffect(() => followWidth(), []);

  // The action log (`issue_reports.md` rule 4): the widget and tab in front,
  // each time it changes, and the page's errors.
  useEffect(() => {
    let front = "";
    const stopLayout = useLayout.subscribe((layout) => {
      const tile = layout.tiles[focusedTileIndex(layout) ?? 0];
      const now = tile ? (tile.tab ? `${tile.widget} · ${tile.tab}` : tile.widget) : "";
      if (now && now !== front) note("open", now);
      front = now;
    });
    const stopErrors = watchErrors();
    return () => {
      stopLayout();
      stopErrors();
    };
  }, []);

  // A recording survives a reload: it is this person's, kept in this browser.
  useEffect(() => useRecorder.getState().bind(), [me.account_id]);

  useEffect(() => {
    const sync = () =>
      void syncNow().then((done) => {
        if (done.reset) void whole();
      });
    const stopLive = startLive({ client, sync });
    const stopOffline = startOffline((done) => void (done.reset && whole()), {
      worker: import.meta.env.PROD,
    });
    return () => {
      stopLive();
      stopOffline();
    };
  }, [client]);

  useEffect(() => {
    const allowed = new Set(widgetsFor(me.owner).map((entry) => entry.key));
    const link = readDeepLink(new URL(window.location.href), (key) => allowed.has(key));
    if (link.open) useLayout.getState().open(link.open);
    if (link.connected) {
      useNotices.getState().push(`${providerName(link.connected)} connected.`);
      void client.invalidateQueries({ queryKey: CONNECTIONS });
    }
    if (link.open || link.connected) window.history.replaceState(null, "", link.cleaned);
  }, [client, me.owner]);

  useEffect(() => {
    void ensureSubscribed();
  }, []);

  useEffect(() => {
    if (location) {
      startTracking(() => usePrefs.getState().setLocation(false));
      return stopTracking;
    }
    // A cleared setting with the grant still held comes back on (v1.82). An
    // "off" the person chose stays off: before 31A it came straight back on,
    // and the browser went on posting where they had said not to.
    if (usePrefs.getState().locationChosen) return undefined;
    void granted().then((yes) => yes && usePrefs.getState().setLocation(true));
    return undefined;
  }, [location]);

  useLive("finance_alert", ({ accounts }) => {
    const names = accounts.map((a) => (a.days ? `${a.name} (${a.days} days)` : a.name)).join(", ");
    useNotices.getState().push(`Balances to update: ${names}.`);
  });
}
